import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Form, { Field } from "components/shared/Form";
import ResourceSelect from "components/shared/inputs/ResourceSelect";
import Button from "components/shared/Button";
import UserShifts from "./UserShifts";
import EventResults from "../../../../Component/components/modals/EventResults";
import { STATISTICS_URL } from "../Widget";
import useApi from "utils/api";
import { useUsersEvents } from "../../hooks/useUsersEvents";
import { useFilters } from "utils/hooks/useFilters";
import { renderUserName } from "utils/helpers/user";
import { withLoadingToast } from "../../../../Component/helpers/withLoadingToast";
import { getApiErrorMessage } from "../../../../Component/hooks/form/useFormActions";
import { createOpenShiftSchema, type OpenShiftFormValues } from "./zodValidation";
import type { TransferResult } from "../../../../Component/components/modals/Transfer/ResponseTable";

const FORM_ID = "create-open-shift-form";

const getUserSearchFilters = (search: string) => ({
    search,
    fields: ["id", "firstName", "lastName", "employeeNumber"],
    withoutLookups: "true",
});

const getUserId = (event: any) => typeof event.user === "object" ? event.user?.id : event.user;

interface CreateOpenShiftFormProps {
    close: () => void;
}

function CreateOpenShiftForm({ close }: CreateOpenShiftFormProps) {
    const { t } = useTranslation();
    const { authPost } = useApi();
    const queryClient = useQueryClient();
    const [filters] = useFilters();
    const schema = useMemo(() => createOpenShiftSchema({ t }), [t]);
    const form = useForm<OpenShiftFormValues>({
        defaultValues: { users: [], shifts: [] },
        resolver: zodResolver(schema),
    });
    const users = useWatch({ control: form.control, name: "users" });
    const { events, loading } = useUsersEvents(users);

    const [results, setResults] = useState<TransferResult[] | null>(null);

    const mutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async ({ users, shifts }: OpenShiftFormValues): Promise<TransferResult[]> => {
            const responses = await Promise.allSettled(
                shifts.map((shift) => {
                    const user = getUserId(shift);
                    return authPost(`/schedules/v2/open-shift/user/${user}`, {
                        data: {
                            user,
                            type: shift.requestType,
                            namespace: "schedule",
                            device: "web-application",
                            data: {
                                shift: shift.id,
                                reason: shift.reason,
                                ...(shift.payAmount !== "" && shift.payAmount != null && {
                                    extraPayment: {
                                        payType: shift.payType,
                                        payAmount: shift.payAmount,
                                    },
                                }),
                            },
                        },
                    });
                })
            );

            return responses.map((response, index) => {
                const shift = shifts[index];
                const user = users.find((user) => user.id === getUserId(shift));
                return response.status === "fulfilled"
                    ? { status: true, schedule: shift, user }
                    : {
                        status: false,
                        message: getApiErrorMessage(response.reason, t("something-went-wrong")),
                        schedule: shift,
                        user,
                    };
            });
        },
        onSuccess: (results) => {
            setResults(results);
            if (results.some((result) => result.status)) {
                queryClient.invalidateQueries({ queryKey: ["/request/open-shifts"] });
                queryClient.invalidateQueries({ queryKey: [STATISTICS_URL] });
            }
            const failedIds = results.filter((result) => !result.status).map((result) => result.schedule.id);
            form.setValue(
                "shifts",
                form.getValues("shifts").filter((shift) => failedIds.includes(shift.id))
            );
        },
    });

    const closeResults = useCallback(() => {
        const allSucceeded = results?.every((result) => result.status);
        setResults(null);
        if (allSucceeded) {
            close();
        }
    }, [results, close]);

    const onSubmit = useCallback((values: OpenShiftFormValues) => {
        const userIds = values.users.map((user) => user.id);
        mutation.mutate({
            ...values,
            shifts: values.shifts.filter((shift) => userIds.includes(getUserId(shift))),
        });
    }, [mutation]);

    return (
        <Form id={FORM_ID} form={form} onFinish={onSubmit} className="tw-flex-1 tw-min-h-0">
            <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
                <Field name="users" label={t("users")} required>
                    <ResourceSelect
                        resourcePath="/schedules/v2/scheduled-users"
                        renderLabel={renderUserName}
                        placeholder={t("users")}
                        mode="multiple"
                        showSearch
                        getSearchFilter={getUserSearchFilters}
                        allowClear
                        params={{
                            startDate: filters.from,
                            endDate: filters.to
                        }}
                    />
                </Field>

                <Field name="shifts" label={t("shifts")} required>
                    <UserShifts
                        users={users}
                        events={events}
                        loading={loading}
                    />
                </Field>
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-4 tw-border-t">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={close}
                    disabled={mutation.isPending}
                >
                    {t("cancel")}
                </Button>
                <Button
                    type="submit"
                    disabled={mutation.isPending}
                    loading={mutation.isPending}
                >
                    {t("create-request")}
                </Button>
            </div>

            <EventResults
                title={t("open-shift-request-results")}
                results={results}
                close={closeResults}
            />
        </Form>
    );
}

export default CreateOpenShiftForm;
